import { useCallback, useMemo, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { useCompetitionsListQuery } from '../../../src/competitions/hooks/useCompetitionsListQuery';
import { useSeasonPickSaver } from '../../../src/competitions/hooks/useSeasonPickSaver';
import {
  buildMatchweeks,
  defaultMatchweekRound,
} from '../../../src/competitions/helpers/matchweekSections.helper';
import type { ApiMatch } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import { SeasonPicksView } from './SeasonPicksView';
import type { SeasonPickValue } from './SeasonMatchPickCard';

/**
 * Season picks container (specs/competitions-hub-client Slice 2).
 * Reuses the parameterized WC matches query; pick writes go through the lean
 * season saver. Local overrides sit on top of server predictions and are
 * reverted when a save fails (AC-5.3).
 */
export function SeasonPicksScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { competitionId: rawCompetitionId } = useLocalSearchParams<{ competitionId: string }>();
  const competitionId = rawCompetitionId ?? '';

  const { data, isLoading, isError } = useWorldCupMatchesQuery(competitionId, competitionId !== '');
  const { data: competitionsList } = useCompetitionsListQuery();

  const [localPicks, setLocalPicks] = useState<Record<string, SeasonPickValue>>({});
  const [selectedRoundState, setSelectedRoundState] = useState<string | null>(null);

  const onSaveError = useCallback((matchId: string) => {
    setLocalPicks((prev) => {
      if (!(matchId in prev)) return prev;
      const next = { ...prev };
      delete next[matchId];
      return next;
    });
  }, []);
  const { schedulePickSave, saveErrorMatchIds } = useSeasonPickSaver({ competitionId, onSaveError });

  const matchweeks = useMemo(() => buildMatchweeks(data?.matches ?? []), [data?.matches]);
  const selectedRound = selectedRoundState ?? defaultMatchweekRound(matchweeks);

  const competition = competitionsList?.find((c) => c.id === competitionId);
  const title = competition?.brand?.name ?? competition?.name ?? 'Season picks';

  const resolvePick = useCallback(
    (match: ApiMatch): SeasonPickValue | null => {
      const local = localPicks[match.matchId];
      if (local) return local;
      if (match.prediction) return { home: match.prediction.home, away: match.prediction.away };
      return null;
    },
    [localPicks],
  );

  const onChangePick = useCallback(
    (matchId: string, home: number, away: number) => {
      setLocalPicks((prev) => ({ ...prev, [matchId]: { home, away } }));
      schedulePickSave(matchId, home, away);
    },
    [schedulePickSave],
  );

  const onRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesStatic(competitionId) });
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesMe(competitionId) });
  }, [queryClient, competitionId]);

  const onBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/app/competitions');
  }, [router]);

  return (
    <SeasonPicksView
      title={title}
      matchweeks={matchweeks}
      selectedRound={selectedRound}
      onSelectRound={setSelectedRoundState}
      resolvePick={resolvePick}
      saveErrorMatchIds={saveErrorMatchIds}
      onChangePick={onChangePick}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onBack={onBack}
      showHeader={false}
    />
  );
}
